import axios from 'axios';

// Use relative URL for same-domain deployments (like Vercel)
// Use full URL only for cross-domain deployments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

export const uploadDocument = async (formData) => {
  try {
    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error.response?.data?.message || 'Failed to upload document';
  }
};

export const getDocuments = async (params = {}) => {
  try {
    const response = await api.get('/documents', { params });
    
    // Handle different response structures
    if (response.data && response.data.status === 'success') {
      return {
        documents: response.data.data.documents || [],
        total: response.data.data.total || 0,
        page: response.data.data.page || 1,
        limit: response.data.data.limit || 10,
        totalPages: response.data.data.totalPages || 1
      };
    } else if (response.data && Array.isArray(response.data.documents)) {
      // Fallback for direct array response
      return {
        documents: response.data.documents || [],
        total: response.data.total || response.data.documents.length,
        page: response.data.page || 1,
        limit: response.data.limit || 10,
        totalPages: response.data.totalPages || 1
      };
    } else if (response.data && Array.isArray(response.data)) {
      // Fallback for simple array response
      return {
        documents: response.data,
        total: response.data.length,
        page: 1,
        limit: response.data.length,
        totalPages: 1
      };
    } else {
      // If response structure is unexpected, return empty result instead of throwing error
      console.warn('Unexpected response structure:', response.data);
      return {
        documents: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
      };
    }
  } catch (error) {
    console.error('Error fetching documents:', error);
    // Only throw error for actual network/HTTP errors, not for empty results
    if (error.response && error.response.status >= 400) {
      throw error.response?.data?.message || 'Failed to fetch documents';
    } else {
      // For other errors, return empty result instead of throwing
      console.warn('Non-critical error fetching documents, returning empty result:', error.message);
      return {
        documents: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1
      };
    }
  }
};

export const getDocument = async (documentId) => {
  try {
    const response = await api.get(`/documents/${documentId}`);
    
    if (!response.data || response.data.message === 'Document not found') {
      throw new Error('Invalid response format: missing document data');
    }
    return response.data;
  } catch (error) {
    console.error('Error in getDocument:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
      documentId
    });
    throw error;
  }
};

export const updateDocument = async (documentId, data) => {
  try {
    const response = await api.put(`/documents/${documentId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating document:', error);
    throw error.response?.data?.message || 'Failed to update document';
  }
};

export const deleteDocument = async (documentId) => {
  try {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error.response?.data?.message || 'Failed to delete document';
  }
}; 